import { AuthN, getConfig, Site } from '@seek/online-identity';
import { createContext, useContext, useEffect, useState } from 'react';

import type { Config } from 'src/types/types';

const AppConfigContext = createContext<Config>({
  appName: 'candidate-basic-search-ui',
  site: 'candidate-seek-au',
  environment: 'development',
  isAuthenticated: false,
  client: 'browser',
  branchName: 'local',
  brand: 'seek',
  country: 'AU',
  appVersion: '',
});

interface AppConfigProviderProps {
  config: Config;
  children: React.ReactNode;
}

export const AppConfigProvider = ({
  config: initialConfig,
  children,
}: AppConfigProviderProps) => {
  const [config, setConfig] = useState<Config>(initialConfig);

  // Re-check auth on the client: the server may report isAuthenticated as false
  // (e.g. no session cookie in SSR) while the user is actually authenticated in
  // the browser. We resolve the real client-side auth status and update config.
  useEffect(() => {
    let isUnmounted = false;
    const resolveClientAuth = async () => {
      try {
        const clientConfig = getConfig(Site.SEEKJOBS, window.location);
        const authN = new AuthN(clientConfig);
        const isAuthenticated = await authN.isAuthenticated();
        if (!isUnmounted) {
          setConfig((prev) => ({ ...prev, isAuthenticated }));
        }
      } catch {
        // On error, leave config unchanged (use initialConfig from state).
      }
    };
    resolveClientAuth();
    return () => {
      // Avoid setState after unmount: if the user navigates away before
      // isAuthenticated() resolves, we skip the update to prevent React warnings
      // and unnecessary work.
      isUnmounted = true;
    };
  }, []);

  return (
    <AppConfigContext.Provider value={config}>
      {children}
    </AppConfigContext.Provider>
  );
};

export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (!context) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};
